import React, { useState, useCallback } from 'react';
import { Box, TextField, Button, Typography, IconButton, InputAdornment, Alert, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoginFormData, LoginPageState, LoginFormValidation } from '../../types/auth';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { MederiBrand } from '../design-system/MederiBrand';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/my-shift';
  const [state, setState] = useState<LoginPageState>({ isLoading: false, error: null, formData: { email: '', password: '' }, showPassword: false });

  const validateForm = useCallback((formData: LoginFormData): LoginFormValidation => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = { isValid: emailRegex.test(formData.email) && formData.email.length > 0, errorMessage: formData.email === '' ? 'メールアドレスを入力してください' : '正しいメールアドレス形式で入力してください' };
    const password = { isValid: formData.password.length >= 6, errorMessage: formData.password === '' ? 'パスワードを入力してください' : 'パスワードは6文字以上で入力してください' };
    return { email, password, isFormValid: email.isValid && password.isValid };
  }, []);

  const handleInputChange = useCallback((field: keyof LoginFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, formData: { ...prev.formData, [field]: event.target.value }, error: null }));
  }, []);

  const handleLogin = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm(state.formData).isFormValid) {
      setState(prev => ({ ...prev, error: '入力内容を確認してください' }));
      return;
    }
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      await login(state.formData.email, state.formData.password);
      navigate(from, { replace: true });
    } catch (error: unknown) {
      const err = error as Error;
      setState(prev => ({ ...prev, error: err.message || 'ログインに失敗しました。メールアドレスとパスワードを確認してください。' }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [state.formData, validateForm, login, navigate, from]);

  const validation = validateForm(state.formData);
  return (
    <Box className="doctor-login" component="main">
      <Box className="doctor-login-brand"><MederiBrand /></Box>
      <Box className="doctor-login-shell">
        <Box className="doctor-login-card">
          <Box className="doctor-login-card-content">
            <Typography component="h1" className="doctor-login-title">ログイン</Typography>
            {state.error && <Alert severity="error" className="doctor-login-alert" onClose={() => setState(prev => ({ ...prev, error: null }))}>{state.error}</Alert>}
            <Box component="form" onSubmit={handleLogin} className="doctor-login-form">
              <Typography component="label" htmlFor="email" className="doctor-login-label">メールアドレス</Typography>
              <TextField required fullWidth id="email" name="email" type="email" autoComplete="email" autoFocus placeholder="メールアドレスを入力" value={state.formData.email} onChange={handleInputChange('email')} error={!validation.email.isValid && state.formData.email !== ''} helperText={!validation.email.isValid && state.formData.email !== '' ? validation.email.errorMessage : ''} disabled={state.isLoading} />
              <Typography component="label" htmlFor="password" className="doctor-login-label doctor-login-password-label">パスワード</Typography>
              <TextField required fullWidth id="password" name="password" type={state.showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="パスワードを入力" value={state.formData.password} onChange={handleInputChange('password')} error={!validation.password.isValid && state.formData.password !== ''} helperText={!validation.password.isValid && state.formData.password !== '' ? validation.password.errorMessage : ''} disabled={state.isLoading} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={state.showPassword ? 'パスワードを隠す' : 'パスワードを表示'} onClick={() => setState(prev => ({ ...prev, showPassword: !prev.showPassword }))} edge="end" disabled={state.isLoading}>{state.showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <Button type="submit" fullWidth variant="contained" className="doctor-login-submit" disabled={state.isLoading || !validation.isFormValid}>{state.isLoading ? <><CircularProgress size={18} color="inherit" />ログイン中...</> : 'ログイン'}</Button>
            </Box>
            <Typography className="doctor-login-support">ログインできない場合は管理者にお問い合わせください</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
